import { GQLQueryOrderInput } from "@rx-cs-system/schema";
import { useCallback } from "react";
import styled from "styled-components";

import { PageBody } from "../PageBody";
import {
  DataListBody,
  DataListBodyItem,
  DataListHeader,
  DataListHeaderItem,
  DataListRow,
  DataTable,
  SwitchHeaderOrder,
} from "../../atoms/Table";
import { SearchInput } from "../../atoms/SearchInput";
import { Typography } from "../../atoms/Typography";
import { LoadMoreButton } from "../../atoms/LoadMoreButton";

/**
 * 医療機関一覧ページ
 */

type MedicalInstitution = {
  id: string;
  name: string;
  prefecture: string;
  phoneNumber: string;
  deletedAt?: string | null;
};

export type MedicalInstitutionProps = {
  medicalInstitutions: MedicalInstitution[];
  searchValue: string;
  order?: GQLQueryOrderInput;
  onSearchClick: (keyword: string) => void;
  onResetClick: () => void;
  onSortClick: (input: GQLQueryOrderInput) => void;
  onRowClick: (id: string) => void;
  onLoadMoreClick: (skip: number) => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 40px;
  height: 100%;
`;

export function MedicalInstitutionPage({
  medicalInstitutions,
  searchValue,
  order,
  onSearchClick,
  onResetClick,
  onSortClick,
  onRowClick,
  onLoadMoreClick,
}: MedicalInstitutionProps) {
  const sortClick = useCallback(
    (value: keyof MedicalInstitution) => {
      onSortClick(SwitchHeaderOrder(value, order));
    },
    [onSortClick, order],
  );

  const header = (
    <>
      <DataListHeaderItem
        onSort={() => sortClick("id")}
        order={order?.field === "id" ? order.value : undefined}
        align="center"
      >
        <Typography size={1} bold>
          ID
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("name")}
        order={order?.field === "name" ? order.value : undefined}
      >
        <Typography size={1} bold>
          医療機関名
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("prefecture")}
        order={order?.field === "prefecture" ? order.value : undefined}
      >
        <Typography size={1} bold>
          都道府県
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem
        onSort={() => sortClick("phoneNumber")}
        order={order?.field === "phoneNumber" ? order.value : undefined}
      >
        <Typography size={1} bold>
          電話番号
        </Typography>
      </DataListHeaderItem>
      <DataListHeaderItem>
        <Typography size={1} bold>
          契約状況
        </Typography>
      </DataListHeaderItem>
    </>
  );

  const searchInput = (
    <SearchInput
      value={searchValue}
      placeholder={"キーワードを入力"}
      onSearch={onSearchClick}
      onReset={onResetClick}
    />
  );

  return (
    <PageBody title="医療機関一覧" searchInput={searchInput}>
      <Container>
        <DataTable>
          <DataListHeader row={header} flexRatio={[3, 5, 2, 2, 2]} />
          <DataListBody
            rows={medicalInstitutions.map(
              (medicalInstitution: MedicalInstitution, index: number) => (
                <DataListRow
                  flexRatio={[3, 5, 2, 2, 2]}
                  key={index}
                  onRowClick={() => onRowClick(medicalInstitution.id)}
                  // onMouseOver={() => rowHover(medicalInstitution.id)}
                  // onMouseLeave={rowLeave}
                >
                  <DataListBodyItem>
                    <Typography size={2} withEllipsis={true}>
                      {medicalInstitution.id}
                    </Typography>
                  </DataListBodyItem>
                  <DataListBodyItem>
                    <Typography size={2} withEllipsis={true}>
                      {medicalInstitution.name}
                    </Typography>
                  </DataListBodyItem>
                  <DataListBodyItem>
                    <Typography size={2} withEllipsis={true}>
                      {medicalInstitution.prefecture}
                    </Typography>
                  </DataListBodyItem>
                  <DataListBodyItem>
                    <Typography size={2}>
                      {medicalInstitution.phoneNumber}
                    </Typography>
                  </DataListBodyItem>
                  <DataListBodyItem>
                    <Typography size={2}>
                      {medicalInstitution.deletedAt ? "退会済み" : "契約中"}
                    </Typography>
                  </DataListBodyItem>
                </DataListRow>
              ),
            )}
          />
        </DataTable>
        <LoadMoreButton
          onClick={() => onLoadMoreClick(medicalInstitutions.length)}
        />
      </Container>
    </PageBody>
  );
}
